import React, { useRef } from 'react';
import { Loader2, ImagePlus, Trash2 } from 'lucide-react';
import { usePhotoUpload } from '../../hooks/usePhotoUpload';
import { SHAPES, FEATURES } from './seatingUtils';

const chip = 'text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] bg-white hover:border-[#B8896C] hover:bg-[#F6EAE0] transition-colors';

const ShapeIcon = ({ shape }) => {
  const cls = 'inline-block border-2 border-[#B8896C] bg-[#FFFDF9] mr-1.5 align-middle';
  if (shape === 'round') return <span className={`${cls} w-3.5 h-3.5 rounded-full`} />;
  if (shape === 'square') return <span className={`${cls} w-3.5 h-3.5 rounded-sm`} />;
  if (shape === 'sweetheart') return <span className={`${cls} w-4 h-2.5 rounded-t-full bg-[#FFF1F3]`} />;
  return <span className={`${cls} w-5 h-2.5 rounded-sm`} />;
};

export const Palette = ({ onAddTable, onAddFeature, room, onRoom, toast }) => {
  const file = useRef(null);
  const { upload, uploading } = usePhotoUpload();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { const r = await upload(f); onRoom({ floorplan_url: r.absoluteUrl }); }
    catch (err) { toast?.({ title: 'Upload failed', description: err.message, variant: 'destructive' }); }
    finally { e.target.value = ''; }
  };
  return (
    <div data-testid="seating-palette" className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide text-[#6A6A6A] w-16">Tables</span>
        {Object.entries(SHAPES).map(([k, v]) => (
          <button key={k} data-testid={`palette-add-${k}`} onClick={() => onAddTable(k)} className={chip}><ShapeIcon shape={k} />{v.label}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide text-[#6A6A6A] w-16">Room</span>
        {Object.entries(FEATURES).filter(([, v]) => !v.area).map(([k, v]) => (
          <button key={k} data-testid={`palette-add-${k}`} onClick={() => onAddFeature(k)} className={chip}>
            <span className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-middle border" style={{ background: v.color, borderColor: v.border }} />{v.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide text-[#6A6A6A] w-16">Areas</span>
        {['indoor', 'outdoor'].map((k) => (
          <button key={k} data-testid={`palette-add-${k}`} onClick={() => onAddFeature(k)} className={chip}>
            <span className="inline-block w-3 h-2.5 rounded-sm mr-1.5 align-middle border border-dashed" style={{ background: FEATURES[k].color, borderColor: FEATURES[k].border }} />{k === 'indoor' ? 'Indoor area' : 'Outdoor area'}
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-[#EFE3D8]" />
        <input ref={file} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="floorplan-file" />
        <button data-testid="floorplan-upload" onClick={() => file.current?.click()} disabled={uploading} className={`${chip} inline-flex items-center gap-1.5`}>
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />} {room.floorplan_url ? 'Replace floor plan' : 'Venue floor plan'}
        </button>
        {room.floorplan_url && (
          <>
            <label className="text-[11px] text-[#6A6A6A] inline-flex items-center gap-1.5">Fade
              <input data-testid="floorplan-opacity" type="range" min="0.1" max="1" step="0.05" value={room.floorplan_opacity ?? 0.5} onChange={(e) => onRoom({ floorplan_opacity: Number(e.target.value) })} className="accent-[#7B2C3B] w-24" />
            </label>
            <button data-testid="floorplan-remove" aria-label="Remove floor plan" onClick={() => onRoom({ floorplan_url: null })} className="text-[#B8896C] hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
          </>
        )}
      </div>
    </div>
  );
};

export default Palette;
